import { Stack, Typography, useTheme } from "@mui/material";
import { useAxesTooltip } from "@mui/x-charts";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { useMouseTouchPosition } from "web_myplann/Shared/Hooks/useMouseTouchPosition";

const TOOLTIP_WIDTH = 180;
const VIEWPORT_PADDING = 16;
const TOOLTIP_OFFSET_Y = 100;

type ChartTooltipRowProps = {
	label: string | null;
	value: string | null;
};

const ChartTooltipRow: FC<ChartTooltipRowProps> = ({ label, value }) => {
	const theme = useTheme();

	return (
		<Stack
			direction="row"
			justifyContent="space-between"
			gap={theme.spacingTokens.xs}
		>
			<Typography variant="caption" color={theme.palette.grey[500]}>
				{label}
			</Typography>
			<Typography variant="button" color={theme.palette.grey[900]} tabular>
				{value}
			</Typography>
		</Stack>
	);
};

export const ChartTooltip: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const pos = useMouseTouchPosition();
	const tooltip = useAxesTooltip();

	if (!tooltip?.[0]) {
		return null;
	}

	const data = tooltip[0];
	const left = Math.min(
		Math.max(pos.x - TOOLTIP_WIDTH / 2, VIEWPORT_PADDING),
		window.innerWidth - (TOOLTIP_WIDTH + VIEWPORT_PADDING),
	);

	return (
		<Stack
			position="absolute"
			left={left}
			top={pos.y - TOOLTIP_OFFSET_Y}
			width={TOOLTIP_WIDTH}
			px={theme.spacingTokens.xs}
			py={theme.spacingTokens.xxs}
			borderRadius={theme.radii.sm}
			bgcolor={theme.palette.grey[100]}
		>
			<ChartTooltipRow
				label={t(Resources.Portfolio.Chart.Date)}
				value={data.axisFormattedValue}
			/>
			{data.seriesItems
				.filter(
					({ value }) => value !== null && value !== undefined && value !== 0,
				)
				.map(({ formattedLabel, formattedValue }) => (
					<ChartTooltipRow
						key={formattedLabel}
						label={formattedLabel}
						value={formattedValue}
					/>
				))}
		</Stack>
	);
};
